import { sortBy } from 'lodash/fp';
import { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { Field, FormSection, InjectedFormProps, reduxForm } from 'redux-form';

import CheckboxField from 'dashboard/components/FormControls/CheckboxField';
import DateInputField from 'dashboard/components/FormControls/DateInputField';
import InputField from 'dashboard/components/FormControls/InputField';
import PhoneInputField from 'dashboard/components/FormControls/PhoneInputField';
import RadioGroup from 'dashboard/components/FormControls/RadioGroup';
import ReactSelectField from 'dashboard/components/FormControls/ReactSelectField';
import TextAreaField from 'dashboard/components/FormControls/TextAreaField';
import ThemedMaskedInput from 'dashboard/components/ThemedMaskedInput';
import AddressInputAutocomplete from 'dashboard/forms/FormCaseAction/AddressInputAutocomplete';
import { FORM_CASE_ACTION } from 'dashboard/lib/constants/formNames';
import { CaseActionWorkflow } from 'dashboard/models/case';
import { InputSchemaType } from 'dashboard/models/trigger';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label } from 'design-system/components/Text';
import { COUNTRIES, CountryCode, US_STATES } from 'lib/constants/locales';
import PiiAttributes from 'lib/interfaces/PiiAttributes';
import { isRequired, isRequiredDate, withMessage } from 'lib/utils/formValidation';
import { assertNever } from 'lib/utils/types';

// The original AddressInput that we were using here used 'street1', 'street2', etc.
// as opposed to 'address-street-1', 'address-street-2', etc. It looks like we are
// moving in the direction of using 'street1', 'street2' instead of prefixing them with
// 'address', and so to be consistent with that future state (and to be consistent for
// backward compatibility), we want to use 'street1' instead of 'address-street-1', which
// is what the LocationSearchInput is coded to look for.
// The LocationSearchInput automatically looks for fields with name PiiAttributes.AddressStreet1,
// etc., and so we need this mapping to correctly update fields when the user
// selects a suggested address.
// THIS IS SOMETHING THAT SHOULD IDEALLY BE CLEANED UP LONGER TERM.
const FIELD_NAME_MAP: Partial<Record<PiiAttributes, string>> = {
  [PiiAttributes.AddressStreet1]: 'street1',
  [PiiAttributes.AddressStreet2]: 'street2',
  [PiiAttributes.AddressCity]: 'city',
  [PiiAttributes.AddressSubdivision]: 'subdivision',
  [PiiAttributes.AddressPostalCode]: 'postalCode',
};

const validate = {
  required: withMessage('Please enter a valid value', isRequired),
  requiredDate: withMessage('Please enter a valid value', isRequiredDate),
};

interface FormCaseActionProps {
  caseAction: CaseActionWorkflow;
  handleCancel: () => void;
}

const FormCaseAction = ({
  caseAction,
  handleSubmit,
  handleCancel,
  submitting,
  change,
  initialValues,
}: FormCaseActionProps & InjectedFormProps<Record<string, string>, FormCaseActionProps>) => {
  const { workflow, trigger } = caseAction;
  const currentFormValue = useSelector((state: RootState) => {
    return state.form[FORM_CASE_ACTION]?.values ?? {};
  });

  const formFields = trigger.attributes['param-schema'].map((param) => {
    if (
      param['required_to_show'].length > 0 &&
      param['required_to_show'].some((req) => !req.values.includes(currentFormValue[req.name]))
    ) {
      return null;
    }
    switch (param.type) {
      case InputSchemaType.Dropdown:
        return (
          <Field
            id={`form-case-action-${workflow.id}__${param.name}`}
            key={`form-case-action-${workflow.id}__${param.name}`}
            data-test={`form-case-action-${workflow.id}__${param.name}`}
            label={param.label}
            name={param.name}
            disabled={param.disabled || submitting}
            placeholder={param.placeholder}
            component={ReactSelectField}
            validate={param.optional ? null : validate.required}
            options={(param.options || []).map((option) => ({
              label: option.label,
              value: option.value,
            }))}
            portal={true}
          />
        );
      case InputSchemaType.Input:
        return (
          <Field
            id={`form-case-action-${workflow.id}__${param.name}`}
            key={`form-case-action-${workflow.id}__${param.name}`}
            data-test={`form-case-action-${workflow.id}__${param.name}`}
            label={param.label}
            name={param.name}
            disabled={param.disabled || submitting}
            placeholder={param.placeholder}
            component={InputField}
            validate={param.optional ? null : validate.required}
          />
        );
      case InputSchemaType.TextField:
        return (
          <Field
            id={`form-case-action-${workflow.id}__${param.name}`}
            key={`form-case-action-${workflow.id}__${param.name}`}
            data-test={`form-case-action-${workflow.id}__${param.name}`}
            label={param.label}
            name={param.name}
            disabled={param.disabled || submitting}
            placeholder={param.placeholder}
            component={TextAreaField}
            validate={param.optional ? null : validate.required}
          />
        );
      case InputSchemaType.CheckBox:
        return (
          <>
            <Field
              id={`form-case-action-${workflow.id}__${param.name}`}
              key={`form-case-action-${workflow.id}__${param.name}`}
              data-test={`form-case-action-${workflow.id}__${param.name}`}
              label={param.label}
              name={param.name}
              disabled={param.disabled || submitting}
              component={CheckboxField}
              validate={param.optional ? null : validate.required}
            />
            <Spacer height={2} />
          </>
        );
      case InputSchemaType.RadioGroup:
        return (
          <Field
            id={`form-case-action-${workflow.id}__${param.name}`}
            key={`form-case-action-${workflow.id}__${param.name}`}
            data-test={`form-case-action-${workflow.id}__${param.name}`}
            label={param.label}
            name={param.name}
            component={RadioGroup}
            disabled={param.disabled || submitting}
            validate={param.optional ? null : validate.required}
            radioLabelValues={(param.options || []).map((option) => ({
              label: option.label,
              value: option.value,
            }))}
            isCompact={true}
          />
        );
      case InputSchemaType.Date:
        return (
          <Field
            id={`form-case-action-${workflow.id}__${param.name}`}
            key={`form-case-action-${workflow.id}__${param.name}`}
            data-test={`form-case-action-${workflow.id}__${param.name}`}
            label={param.label}
            name={param.name}
            disabled={param.disabled || submitting}
            component={DateInputField}
            validate={param.optional ? null : validate.requiredDate}
            yearMax={null}
          />
        );
      case InputSchemaType.Phone:
        return (
          <Field
            id={`form-case-action-${workflow.id}__${param.name}`}
            key={`form-case-action-${workflow.id}__${param.name}`}
            data-test={`form-case-action-${workflow.id}__${param.name}`}
            label={param.label}
            name={param.name}
            disabled={param.disabled || submitting}
            component={PhoneInputField}
            validate={param.optional ? null : validate.required}
          />
        );
      case InputSchemaType.Address: {
        return (
          <div
            key={`form-case-action-${workflow.id}__${param.name}`}
            id={`form-case-action-${workflow.id}__${param.name}`}
          >
            <FormSection name={param.name} component={Fragment}>
              <Label size='lg' weight='bold'>
                {param.label}
              </Label>
              <AddressInputAutocomplete
                data-test={`form-case-action-${workflow.id}__${param.name}`}
                boundChangeFormValue={(field: string, value: string) => {
                  change(`${param.name}.${FIELD_NAME_MAP[field as PiiAttributes]}`, value);
                }}
                inputCountryCode={
                  (initialValues?.[`${param.name}.countryCode`] as CountryCode) ?? 'US'
                }
                disabled={param.disabled || submitting}
                fieldRequired={!param.optional}
              />
            </FormSection>
          </div>
        );
      }
      case InputSchemaType.Country:
        return (
          <Field
            id={`form-case-action-${workflow.id}__${param.name}`}
            key={`form-case-action-${workflow.id}__${param.name}`}
            data-test={`form-case-action-${workflow.id}__${param.name}`}
            label={param.label}
            name={param.name}
            disabled={param.disabled || submitting}
            component={ReactSelectField}
            validate={param.optional ? null : validate.required}
            options={sortBy(
              'label',
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              Object.keys(COUNTRIES).map((countryCode: CountryCode) => ({
                value: countryCode,
                label: COUNTRIES[countryCode],
              })),
            )}
            portal={true}
          />
        );
      case InputSchemaType.MaskedInput:
        return (
          <Field
            id={`form-case-action-${workflow.id}__${param.name}`}
            key={`form-case-action-${workflow.id}__${param.name}`}
            data-test={`form-case-action-${workflow.id}__${param.name}`}
            label={param.label}
            name={param.name}
            disabled={param.disabled || submitting}
            component={ThemedMaskedInput}
            mask={[/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
            validate={param.optional ? null : validate.required}
          />
        );
      case InputSchemaType.USState:
        return (
          <Field
            id={`form-case-action-${workflow.id}__${param.name}`}
            key={`form-case-action-${workflow.id}__${param.name}`}
            data-test={`form-case-action-${workflow.id}__${param.name}`}
            label={param.label}
            name={param.name}
            disabled={param.disabled || submitting}
            component={ReactSelectField}
            options={sortBy(
              'label',
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              Object.keys(US_STATES).map((code: keyof typeof US_STATES) => ({
                label: US_STATES[code],
                value: US_STATES[code],
              })),
            )}
            validate={param.optional ? null : validate.required}
            portal={true}
          />
        );
      case InputSchemaType.USStateCode:
        return (
          <Field
            id={`form-case-action-${workflow.id}__${param.name}`}
            key={`form-case-action-${workflow.id}__${param.name}`}
            data-test={`form-case-action-${workflow.id}__${param.name}`}
            label={param.label}
            name={param.name}
            disabled={param.disabled || submitting}
            component={ReactSelectField}
            options={sortBy(
              'label',
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              Object.keys(US_STATES).map((code: keyof typeof US_STATES) => ({
                label: US_STATES[code],
                value: code,
              })),
            )}
            validate={param.optional ? null : validate.required}
            portal={true}
          />
        );
      default:
        assertNever(param.type);
    }
  });

  return (
    <form onSubmit={handleSubmit} data-test={`form-case-action-${workflow.id}`}>
      {formFields}
      <HStack equal={true}>
        <Button
          id={`form-case-action-${workflow.id}__cancel`}
          disabled={submitting}
          variant='basic'
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          type='submit'
          id={`form-case-action-${workflow.id}__submit`}
          disabled={submitting}
          isPending={submitting}
        >
          Submit
        </Button>
      </HStack>
    </form>
  );
};

export default reduxForm<{}, FormCaseActionProps>({
  form: FORM_CASE_ACTION,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
})(FormCaseAction);
